import React, { useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import './Alert.scss';

export type AlertVariant = 'success' | 'warning' | 'failure';

export interface AlertProps {
  open: boolean;
  /** Title shown on the first line next to the icon */
  title: React.ReactNode;
  /** Description on the second line (previously 'message') */
  message: React.ReactNode;
  onClose: () => void;
  variant?: AlertVariant;
  /** Auto close in ms. Set 0 or undefined to disable. Default: 10000 (10s) */
  autoCloseMs?: number;
  /** Accessible label for the alert region */
  ariaLabel?: string;
  /** Customize the Cancel label; default: 'Cancel' */
  closeLabel?: string;
  /** Provide a custom icon for the cancel action (left of label) */
  closeIcon?: React.ReactNode;
  /** Optional test id */
  dataTestId?: string;
}

const VariantIcon: React.FC<{ variant: AlertVariant; className?: string; title?: string }> = ({
  variant,
  className,
  title,
}) => {
  // Simple inline SVGs, tinted via CSS currentColor
  switch (variant) {
    case 'success':
      return (
        <EonUiIcon name="info" size="normal"/>
      );
    case 'warning':
      return (
        <EonUiIcon name="alert" size="normal"/>
      );
    case 'failure':
      return (
        <EonUiIcon name="alert" size="normal"/>
      );
  }
};

export const Alert: React.FC<AlertProps> = ({
  open,
  title,
  message,
  onClose,
  variant = 'success',
  autoCloseMs = 5000,
  ariaLabel,
  dataTestId,
  closeLabel = 'Cancel',
  closeIcon,
}) => {
  const timerRef = useRef<number | null>(null);
  const titleId = useId();
  const descId = useId();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Auto close
  useEffect(() => {
    clearTimer();
    if (open && autoCloseMs && autoCloseMs > 0) {
      timerRef.current = window.setTimeout(() => {
        onClose?.();
      }, autoCloseMs);
    }
    return clearTimer;
  }, [open, autoCloseMs, onClose, clearTimer]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const ariaLive = variant === 'failure' ? 'assertive' : 'polite';

  return createPortal(
    <div
      className={`rk-alert rk-alert--${variant}`}
      role="alert"
      aria-live={ariaLive}
      aria-label={ariaLabel}
      aria-labelledby={titleId}
      aria-describedby={descId}
      data-testid={dataTestId}
    >
      <div className="rk-alert__grid">
        {/* Row 1: icon + title */}
        <VariantIcon
          variant={variant}
          className="rk-alert__icon"
          title={typeof title === 'string' ? title : undefined}
        />
        <div className="rk-alert__title" id={titleId}>
          {title}
        </div>

        {/* Row 2: description */}
        <div className="rk-alert__desc" id={descId}>
          {message}
        </div>

        {/* Row 3: close with icon + Cancel */}
        <div className="rk-alert__actions">
          <button
            type="button"
            className="rk-alert__cancel"
            onClick={onClose}
            aria-label={typeof closeLabel === 'string' ? closeLabel : 'Cancel alert'}
          >
            {/* Provided icon or a default icon */}
            {closeIcon ?? <EonUiIcon name="cross" />}
            <span>{closeLabel}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
